import { useNavigate } from "@tanstack/react-location"
import { useQueryClient } from "@tanstack/react-query"
import { isFetchError } from "fetcher"
import { toast } from "materia"
import { useTranslation } from "react-i18next"
import { useChat, useChatState } from "../../hooks/useChat"
import { useChatDirectory } from "../../hooks/useChatDirectory"
import { useGroup } from "../../hooks/useGroup"
import type { PeerProfile } from "../../types"
import { jidLocalPart } from "../../xmpp/jid"
import { ChatModal } from "../ChatModal"
import { PeoplePicker, type Colleague } from "../NewChat"
import { GroupConfirm } from "./GroupConfirm"
import type { GroupDialog } from "./GroupMenu"
import { ManageMembers, type GroupMember } from "./ManageMembers"
import { RenameGroup } from "./RenameGroup"

type GroupDialogsConnectedProps = {
  dialog: GroupDialog
  /** The room-JID localpart. */
  groupId: string
  name: string
  /** Member handles, as the conversation row reports them (FAME-657). */
  memberHandles: string[]
  isManager: boolean
  /**
   * Passed in rather than resolved here: `usePeerProfiles` is called once at
   * the top of the view with every visible identity, members included.
   */
  userHandleToPeerProfile: (handle: string) => PeerProfile
  /** Removing or promoting a member takes a user id, not a handle. */
  resolveUserId: (handle: string) => string | null
  onClose: () => void
}

/** The one group dialog the menu opened. */
export function GroupDialogsConnected({
  dialog,
  groupId,
  name,
  memberHandles,
  isManager,
  userHandleToPeerProfile,
  resolveUserId,
  onClose,
}: GroupDialogsConnectedProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    renameGroup,
    setGroupArchived,
    addGroupMembers,
    removeGroupMembers,
    promoteGroupAdmin,
    demoteGroupAdmin,
  } = useChat()
  const colleagues = useChatDirectory()
  const selfJid = useChatState((state) => state.selfJid)
  const group = useGroup(groupId)
  const ownHandle = selfJid ? jidLocalPart(selfJid) : undefined

  // Admins live on the group record, not the row, so refetch it rather than
  // reseed the list (FAME-574).
  const setAdmin = async (member: GroupMember, admin: boolean) => {
    const userId = resolveUserId(member.handle)
    if (!userId) return

    try {
      await (admin
        ? promoteGroupAdmin(groupId, userId)
        : demoteGroupAdmin(groupId, userId))
      await queryClient.invalidateQueries({
        queryKey: ["chat", "group", groupId],
      })
    } catch (error) {
      toast(String(t(groupRefusalLine(error))))
    }
  }

  const removeMember = async (member: GroupMember) => {
    const userId = resolveUserId(member.handle)
    if (!userId) return

    try {
      await removeGroupMembers(groupId, [userId])
    } catch (error) {
      toast(String(t(groupRefusalLine(error))))
    }
  }

  // Leaving is removing exactly yourself (FAME-794) — the same endpoint, no new
  // route. Any member may do it, the owner included; the server repairs an
  // ownerless group on the way out.
  const leaveGroup = async () => {
    const userId = ownHandle ? resolveUserId(ownHandle) : null
    if (!userId) return

    try {
      await removeGroupMembers(groupId, [userId])
      // The server drops our conversation row and reseed has already taken it
      // out of the list, so the open thread is gone — step back to it.
      onClose()
      navigate({ to: "/messages" })
    } catch (error) {
      toast(String(t(groupRefusalLine(error, "chat.REFUSAL_LEAVE"))))
    }
  }

  switch (dialog) {
    case "rename":
      return (
        <RenameGroup
          name={name}
          onRename={(next) => {
            void renameGroup(groupId, next)
            onClose()
          }}
          onClose={onClose}
        />
      )

    case "add": {
      // Colleagues carry user ids, memberHandles carry chat handles — different
      // namespaces, so resolve the handles before excluding existing members.
      const memberIds = new Set(
        memberHandles
          .map(resolveUserId)
          .filter((id): id is string => id !== null)
      )
      return (
        <ChatModal ariaLabel={String(t("chat.ADD_MEMBERS"))} onClose={onClose}>
          <PeoplePicker
            title={String(t("chat.ADD_MEMBERS"))}
            confirmLabel={String(t("chat.ADD_MEMBERS"))}
            colleagues={(colleagues ?? []).filter(
              (one) => !memberIds.has(one.id)
            )}
            onConfirm={(chosen: Colleague[]) => {
              // One request for the whole selection — the endpoint takes a list.
              void addGroupMembers(
                groupId,
                chosen.map((person) => person.id)
              )
              onClose()
            }}
          />
        </ChatModal>
      )
    }

    case "members":
    case "admins":
      return (
        <ManageMembers
          mode={dialog === "admins" ? "admins" : isManager ? "remove" : "view"}
          members={memberHandles.map((handle) => ({
            ...userHandleToPeerProfile(handle),
            handle,
            isOwner: handle === group?.owner,
            isAdmin: group?.admins.includes(handle) ?? false,
            isSelf: handle === ownHandle,
          }))}
          onRemove={(member) => void removeMember(member)}
          onPromote={(member) => void setAdmin(member, true)}
          onDemote={(member) => void setAdmin(member, false)}
          onClose={onClose}
        />
      )

    case "archive":
      return (
        <GroupConfirm
          title={String(t("chat.ARCHIVE_GROUP_TITLE"))}
          body={String(t("chat.ARCHIVE_GROUP_BODY"))}
          confirmLabel={String(t("chat.ARCHIVE_GROUP"))}
          onConfirm={() => {
            void setGroupArchived(groupId, true)
            onClose()
          }}
          onCancel={onClose}
        />
      )

    case "leave":
      return (
        <GroupConfirm
          title={String(t("chat.LEAVE_GROUP_TITLE"))}
          body={String(t("chat.LEAVE_GROUP_BODY"))}
          confirmLabel={String(t("chat.LEAVE_GROUP"))}
          onConfirm={() => void leaveGroup()}
          onCancel={onClose}
        />
      )
  }
}

const GROUP_REFUSAL_LINES: Record<string, string> = {
  owner_immutable: "chat.REFUSAL_OWNER_IMMUTABLE",
  last_manager: "chat.REFUSAL_LAST_MANAGER",
  not_a_member: "chat.REFUSAL_NOT_A_MEMBER",
  forbidden: "chat.REFUSAL_FORBIDDEN",
}

function groupRefusalLine(
  error: unknown,
  fallback = "chat.REFUSAL_GROUP"
): string {
  if (isFetchError(error) && error.json && typeof error.json === "object") {
    const body = error.json as { error?: unknown; code?: unknown }
    const code = typeof body.error === "string" ? body.error : body.code
    if (typeof code === "string" && GROUP_REFUSAL_LINES[code])
      return GROUP_REFUSAL_LINES[code]
  }
  return fallback
}
